import { Injectable, inject } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { RANDOM_FRACTION } from 'src/core/helpers/jitter.helper';
import { HubServiceConfigModel } from 'src/core/models/signalr/hub-service-config.model';
import { AuthorizationService } from '../auth/authorization.service';
import { JitteredRetryPolicy } from './jittered-retry-policy';

@Injectable()
export abstract class HubService {
  private readonly authorizationService = inject(AuthorizationService);
  protected readonly randomFraction = inject(RANDOM_FRACTION);
  private readonly connectionState = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private readonly defaultReconnectDelays = [0, 2000, 5000, 10000, 30000];
  private startPromise: Promise<void> | null = null;
  private config: HubServiceConfigModel | null = null;

  protected hubConnection: HubConnection | null = null;

  protected initialize(config: HubServiceConfigModel): void {
    this.config = config;
  }

  getConnection(): HubConnection | null {
    return this.hubConnection;
  }

  getConnectionId(): string | null {
    return this.hubConnection?.connectionId ?? null;
  }

  getConnectionState(): Observable<HubConnectionState> {
    return this.connectionState.asObservable();
  }

  protected isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  protected async startConnection(): Promise<void> {
    if (!this.hubConnection) {
      if (!this.config) {
        throw new Error('HubService is not initialized.');
      }

      await firstValueFrom(this.authorizationService.getAccessKey());
      this.hubConnection = this.buildConnection(this.config);
      this.registerConnectionEvents(this.hubConnection);
      this.setConnectionState(this.hubConnection.state);
    }

    if (this.hubConnection.state !== HubConnectionState.Disconnected) {
      return;
    }

    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.hubConnection.start()
      .then(async () => {
        this.setConnectionState(this.hubConnection?.state ?? HubConnectionState.Disconnected);
        await this.onConnected();
      })
      .catch(error => {
        this.setConnectionState(HubConnectionState.Disconnected);
        throw error;
      })
      .finally(() => {
        this.startPromise = null;
      });

    return this.startPromise;
  }

  protected async stopConnection(methodName?: string): Promise<void> {
    const connection = this.hubConnection;
    this.hubConnection = null;
    this.startPromise = null;

    if (!connection) {
      this.setConnectionState(HubConnectionState.Disconnected);
      return;
    }

    try {
      if (methodName && connection.state === HubConnectionState.Connected) {
        await connection.invoke(methodName);
      }
    } catch {
      // Swallow hub cleanup errors during shutdown.
    }

    try {
      await connection.stop();
    } catch {
      // Swallow transport errors during shutdown.
    }

    this.setConnectionState(HubConnectionState.Disconnected);
  }

  protected async invoke<TResult = void>(methodName: string, ...args: unknown[]): Promise<TResult | undefined> {
    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      return undefined;
    }

    return this.hubConnection.invoke<TResult>(methodName, ...args);
  }

  protected abandonConnection(): void {
    const connection = this.hubConnection;
    this.hubConnection = null;
    this.startPromise = null;
    this.setConnectionState(HubConnectionState.Disconnected);

    if (connection) {
      void connection.stop();
    }
  }

  protected async onConnected(): Promise<void> {
    return Promise.resolve();
  }

  protected async onReconnected(): Promise<void> {
    return Promise.resolve();
  }

  protected onClosed(): void {
    // Optional for subclasses.
  }

  private buildConnection(config: HubServiceConfigModel): HubConnection {
    return new HubConnectionBuilder()
      .configureLogging(config.logLevel ?? LogLevel.Warning)
      .withUrl(config.url, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect(
        new JitteredRetryPolicy(config.reconnectDelays ?? this.defaultReconnectDelays, this.randomFraction),
      )
      .build();
  }

  private registerConnectionEvents(connection: HubConnection): void {
    connection.onclose(() => {
      // A stopped or abandoned connection may close late; only the current one drives state and restarts.
      if (connection !== this.hubConnection) {
        return;
      }

      this.setConnectionState(connection.state);
      this.onClosed();
    });

    connection.onreconnecting(() => {
      this.setConnectionState(connection.state);
    });

    connection.onreconnected(async () => {
      this.setConnectionState(connection.state);

      try {
        await this.onReconnected();
      } catch (error) {
        console.error('[HubService] Failed to restore hub state after reconnect', error);
      }
    });
  }

  private setConnectionState(state: HubConnectionState): void {
    this.connectionState.next(state);
  }
}
